import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Filter, 
  Search, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  Download,
  RefreshCw,
  Trash2
} from 'lucide-react';
import apiService from '../../services/api';
import ApplicationDetailModal from '../../components/admin/ApplicationDetailModal';

const AdminDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  const ACCESS_PASSWORD = 'PASSword123$';

  useEffect(() => {
    const isAuth = sessionStorage.getItem('bandn_admin_auth');
    if (isAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchApplications();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, statusFilter]);

  const handleAuth = (e) => {
    e.preventDefault();
    if (password === ACCESS_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      sessionStorage.setItem('bandn_admin_auth', 'true');
    } else {
      setAuthError('Invalid password. Please try again.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('bandn_admin_auth');
    setPassword('');
  };

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getApplications();
      setApplications(data.data.applications);
    } catch (err) {
      setError(err.message || 'Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        app.applicantName.toLowerCase().includes(term) ||
        app.email.toLowerCase().includes(term) ||
        app.phone.includes(term) ||
        app.position.toLowerCase().includes(term)
      );
    }

    setFilteredApplications(filtered);
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setShowDetailModal(true);
  };

  const handleStatusUpdate = async (applicationId, newStatus, notes) => {
    try {
      await apiService.updateApplicationStatus(applicationId, newStatus, notes);
      await fetchApplications();
      setShowDetailModal(false);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      const allIds = filteredApplications.map(app => app.applicationId);
      if (allIds.length > 100) {
        alert('Cannot select more than 100 items at once. Use "Select Top 100" instead.');
        return;
      }
      setSelectedIds(allIds);
      setSelectAll(true);
    }
  };

  const handleSelectTop100 = () => {
    const top100Ids = filteredApplications.slice(0, 100).map(app => app.applicationId);
    setSelectedIds(top100Ids);
    setSelectAll(false);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  const handleSelectOne = (applicationId) => {
    if (selectedIds.includes(applicationId)) {
      setSelectedIds(selectedIds.filter(id => id !== applicationId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedIds, applicationId];
      setSelectedIds(newSelected);
      if (newSelected.length === filteredApplications.length) {
        setSelectAll(true);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getQualificationLabel = (value) => {
    const labels = {
      'phd': 'PhD / Doctorate',
      'masters': "Master's Degree",
      'postgrad-diploma': 'Postgraduate Diploma',
      'bachelors': "Bachelor's Degree",
      'diploma': 'Diploma',
      'certificate': 'Certificate III/IV',
      'secondary': 'Year 12 / HSC',
      'other': 'Other'
    };
    return labels[value] || value || 'N/A';
  };

  const handleExportCSV = () => {
    const appsToExport = selectedIds.length > 0 
      ? filteredApplications.filter(app => selectedIds.includes(app.applicationId))
      : filteredApplications;

    if (appsToExport.length === 0) {
      alert('No applications to export');
      return;
    }

    const headers = [
      'Date',
      'Full Name',
      'Email',
      'Phone',
      'Residence',
      'Nationality',
      'Role/Position',
      'Qualification',
      'Program',
      'Qualification Country'
    ];

    const rows = appsToExport.map(app => {
      const data = app.applicationData || {};
      return [
        formatDate(app.createdAt),
        app.applicantName,
        app.email,
        data.phone || '',
        data.currentCountry || '',
        data.nationality || '',
        app.position,
        getQualificationLabel(data.qualification),
        data.fieldOfStudy || '',
        data.qualificationCountry || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bandn_applications_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('No applications selected');
      return;
    }

    if (selectedIds.length > 100) {
      alert('Cannot delete more than 100 applications at once. Please select up to 100 items.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} application(s)? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const result = await apiService.deleteApplications(selectedIds);
      await fetchApplications();
      setSelectedIds([]);
      setSelectAll(false);
      alert(`Successfully deleted ${result.data.deleted} application(s)`);
    } catch (err) {
      console.error('Failed to delete applications:', err);
      alert(`Failed to delete applications: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: 'bg-blue-100 text-blue-800',
      reviewing: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      'on-hold': 'bg-gray-100 text-gray-800'
    };

    const icons = {
      submitted: <Clock size={14} />,
      reviewing: <RefreshCw size={14} />,
      approved: <CheckCircle size={14} />,
      rejected: <XCircle size={14} />,
      'on-hold': <Clock size={14} />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-normal ${styles[status] || styles.submitted}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const stats = {
    total: applications.length,
    submitted: applications.filter(a => a.status === 'submitted').length,
    reviewing: applications.filter(a => a.status === 'reviewing').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-bnCream flex items-center justify-center px-6">
        <motion.div
          className="max-w-md w-full bg-white border border-bnBorder rounded-2xl shadow-sm p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-8">
            <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-4">
              Admin Access
            </p>
            <h2 className="font-display font-light text-2xl text-bnInk mb-2">Admin Access Required</h2>
            <p className="text-bnBody text-sm leading-relaxed">
              Please enter the password to access the application management system.
            </p>
          </div>

          <form onSubmit={handleAuth}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm text-bnInk mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-bnBorder rounded-lg focus:outline-none focus:border-bnAccent transition-colors"
                placeholder="Enter access password"
                required
              />
            </div>

            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {authError}
              </div>
            )}

            <button
              type="submit"
              className="w-full inline-flex items-center justify-center bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm py-3.5 px-4 rounded-full transition-colors"
            >
              Access Management System
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <section className="bg-bnCream py-12 md:py-16 border-b border-bnBorder">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <div>
              <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-4">
                Admin
              </p>
              <h1 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl mb-2">Application Management</h1>
              <p className="text-bnBody text-base leading-relaxed">Review and manage job applications</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleLogout}
                className="text-bnBody hover:text-bnAccent transition-colors text-sm"
              >
                Logout
              </button>
              <button
                onClick={fetchApplications}
                className="inline-flex items-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3 rounded-full transition-colors"
              >
                <RefreshCw size={18} />
                Refresh
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="container mx-auto px-6 lg:px-12 py-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-bnBorder rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-bnBody">Total</p>
                <p className="font-display font-light text-3xl text-bnInk mt-1">{stats.total}</p>
              </div>
              <Users className="text-bnAccent" size={32} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-bnBorder rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-bnBody">New</p>
                <p className="font-display font-light text-3xl text-blue-600 mt-1">{stats.submitted}</p>
              </div>
              <Clock className="text-blue-400" size={32} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-bnBorder rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-bnBody">Reviewing</p>
                <p className="font-display font-light text-3xl text-yellow-600 mt-1">{stats.reviewing}</p>
              </div>
              <RefreshCw className="text-yellow-400" size={32} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-bnBorder rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-bnBody">Approved</p>
                <p className="font-display font-light text-3xl text-green-600 mt-1">{stats.approved}</p>
              </div>
              <CheckCircle className="text-green-400" size={32} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-bnBorder rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-bnBody">Rejected</p>
                <p className="font-display font-light text-3xl text-red-600 mt-1">{stats.rejected}</p>
              </div>
              <XCircle className="text-red-400" size={32} />
            </div>
          </motion.div>
        </div>

        <div className="bg-white border border-bnBorder rounded-2xl">
          <div className="p-6 border-b border-bnBorder">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-bnBody" size={20} />
                  <input
                    type="text"
                    placeholder="Search by name, email, phone, or position..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-bnBorder rounded-lg focus:outline-none focus:border-bnAccent transition-colors"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter size={20} className="text-bnBody" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-bnBorder rounded-lg focus:outline-none focus:border-bnAccent transition-colors"
                  >
                    <option value="all">All Status</option>
                    <option value="submitted">Submitted</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="on-hold">On Hold</option>
                  </select>
                </div>
              </div>
              
              {selectedIds.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-3 bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-blue-800 font-normal">
                        {selectedIds.length} selected {selectedIds.length > 100 && <span className="text-red-600 font-normal">(Max 100 for delete)</span>}
                      </span>
                      <button
                        onClick={handleClearSelection}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        Clear Selection
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleExportCSV}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-bnInk text-white rounded-full hover:opacity-90 transition-colors text-sm"
                      >
                        <Download size={16} />
                        Export Selected
                      </button>
                      <button
                        onClick={handleBulkDelete}
                        disabled={selectedIds.length > 100}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={16} />
                        Delete Selected
                      </button>
                    </div>
                  </div>
                  {selectedIds.length > 100 && (
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                      ⚠️ You have selected {selectedIds.length} items. Delete is limited to 100 items at a time to prevent timeouts. Please reduce your selection or use "Select Top 100".
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSelectTop100}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-bnInk text-bnInk rounded-full hover:bg-bnInk hover:text-white transition-colors text-sm"
                >
                  <CheckCircle size={16} />
                  Select Top 100
                </button>
                <button
                  onClick={handleExportCSV}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-bnBorder text-bnInk rounded-full hover:border-bnAccent hover:text-bnAccent transition-colors text-sm"
                >
                  <Download size={16} />
                  Export All as CSV
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center">
                <RefreshCw className="animate-spin mx-auto mb-4 text-bnAccent" size={32} />
                <p className="text-bnBody">Loading applications...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <XCircle className="mx-auto mb-4 text-red-400" size={32} />
                <p className="text-red-600">{error}</p>
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="mx-auto mb-4 text-bnBody" size={32} />
                <p className="text-bnBody">No applications found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-bnCream border-b border-bnBorder">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 accent-bnAccent border-bnBorder rounded"
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Full Name
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Residence
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Nationality
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Qualification
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Program
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left">
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-bnBorder">
                  {filteredApplications.map((app) => (
                    <tr key={app.applicationId} className="hover:bg-bnCream transition-colors">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(app.applicationId)}
                          onChange={() => handleSelectOne(app.applicationId)}
                          className="w-4 h-4 accent-bnAccent border-bnBorder rounded"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-bnInk">{formatDate(app.createdAt)}</div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div 
                          className="text-xs text-bnInk max-w-[200px] truncate" 
                          title={app.applicantName}
                        >
                          {app.applicantName}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div 
                          className="text-xs text-bnInk max-w-[250px] truncate" 
                          title={app.email}
                        >
                          {app.email}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-bnInk">{app.applicationData?.phone || 'N/A'}</div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-bnInk">{app.currentCountry}</div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-bnInk">{app.applicationData?.nationality || 'N/A'}</div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-bnInk">{app.position}</div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-bnInk">{getQualificationLabel(app.applicationData?.qualification)}</div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div 
                          className="text-xs text-bnInk max-w-[180px] truncate" 
                          title={app.applicationData?.fieldOfStudy || 'N/A'}
                        >
                          {app.applicationData?.fieldOfStudy || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {getStatusBadge(app.status)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          onClick={() => handleViewDetails(app)}
                          className="text-bnAccent hover:text-bnAccentDark transition-colors"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>

      {showDetailModal && selectedApplication && (
        <ApplicationDetailModal
          application={selectedApplication}
          onClose={() => setShowDetailModal(false)}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
